import { Chat, ChatContent } from '@glade-ai/chat-plugin/ui';
import React, { type FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { type Descendant } from 'slate';

import PaginationObserver from '@/components/PaginationObserver';
import ProgressIndicator from '@/components/ProgressIndicator';
import { ConversationComposeBox } from '@/containers/chat-composeboxes/ConversationComposeBox';
import { ConversationMessage, type ConversationMessageType } from '@/containers/chat-messages/ConversationMessage';
import { type ChatMessage } from '@/containers/chats/ConversationChat/types';
import { useUser } from '@/hooks';
import useNoodleApi from '@/hooks/useNoodleApi';
import { useIsUserInitialized } from '@/providers/Auth';
import { SocketMessageType } from '@/providers/Socket/SocketContext';
import { type NewChatMessageData } from '@/providers/Socket/typings';
import useSocketContext from '@/providers/Socket/useSocketContext';
import TeamsContext from '@/providers/Teams/TeamsContext';
import { getMessageThread } from '@/tsClient';
import * as tsClient from '@/tsClient';
import { type MediaApiModels } from '@/tsClient/servers/noodleMedia';
import * as ApiModels from '@/typings/api-models';
import type * as Types from '@/typings/graphql-models';

type Props = {
  conversationSid: string;
  onSendReply: () => void;
  messageId: string;
  onCloseThread?: () => void;
  editMessageFn?: (data: {
    id: string;
    text?: { children: Descendant[] }[];
    medias?: { id: string }[];
    handbooks?: { id: string }[];
    attachments?: ApiModels.CreateMessageAttachment[];
  }) => Promise<void>;
};

type Children = Awaited<ReturnType<typeof getMessageThread>>['children'];

type Medias = Children[number]['medias'];

type Message = ConversationMessageType &
  Pick<ApiModels.Message, 'id' | 'createdAt' | 'isDeleted' | 'text' | 'title' | 'type' | 'isPinned'> & {
    canReply?: boolean;
    canReplyForFree?: boolean;
    areCommentsHidden?: boolean;
    isContentHidden?: boolean;
    isAvailableForFree?: boolean;
    isAIGenerated?: boolean;
    owner?: {
      id: string;
      name?: string | null;
      image?: {
        url: string;
      } | null;
    } | null;
    tags: {
      id: string;
      name: string;
    }[];
    children?: Children;
    medias: Pick<MediaApiModels.Media, 'id' | 'vimeoUri' | 'type' | 's3OriginId' | 'url' | 'name'>[];
    viewPermissions: { id: string }[];
    commentPermissions: { id: string }[];
    replyPermissions: { id: string }[];
    product?: {
      creator: Pick<Types.Creator, 'id' | 'personId' | 'name'>;
    };
  };

const PAGE_SIZE = 50;

const ConversationThreadPage: FC<Props> = ({
  conversationSid,
  messageId,
  onSendReply,
  editMessageFn,
  onCloseThread,
}) => {
  const [user] = useUser();
  const { creatorId } = useContext(TeamsContext);
  const userId = user?.id;
  const userIsInitialized = useIsUserInitialized();
  const [message, setMessage] = useState<Message | null>(null);
  const { getData: getMessageThreadFn } = useNoodleApi(getMessageThread);
  const { data: creator, getData: getConversationCreatorFn } = useNoodleApi(
    tsClient.conversations.getConversationCreator,
  );
  const { getData: postConversationMessageFn } = useNoodleApi(tsClient.conversations.postConversationMessage, {
    healthMonitor: { name: 'send-message' },
  });
  const { getData: getConversationMessageFn } = useNoodleApi(tsClient.messages.getConversationMessage);
  const { addListener, removeListener, isInitialized: isSocketInitialized } = useSocketContext();
  const [children, setChildren] = useState<Awaited<ReturnType<typeof getMessageThread>>['children']>([]);
  const [totalChildren, setTotalChildren] = useState(0);
  const currentPageRef = useRef(1); // use ref instead of state to avoid potential races with multiple clicks.
  const [isInitializing, setIsInitializing] = useState(true);
  const [creatorPersonId, setCreatorPersonId] = useState<string | null>(null);
  const [creatorSlug, setCreatorSlug] = useState<string | null>(null);
  const [isPostingComment, setIsPostingComment] = useState<boolean>(false);
  const [currentMessage, setCurrentMessage] = useState<ChatMessage | null | undefined>(null);
  const hasCreatorPermissions = creatorId === creator?.id;

  const handleDeleteMessage = useCallback(
    (_parentMessageId: string, deletedMessageId: string | undefined): void => {
      if (deletedMessageId) {
        // A child reply was deleted - remove it from the children array
        setChildren((previous) => previous.filter((child) => child.id !== deletedMessageId));
        setTotalChildren((previous) => Math.max(0, previous - 1));
      } else {
        // The root thread message was deleted - close the thread
        onCloseThread?.();
      }
    },
    [onCloseThread],
  );

  const initializeMessage = async (): Promise<void> => {
    currentPageRef.current = 1;
    const messageResponse = await getMessageThreadFn({
      creatorSlug: 'not-required',
      messageId,
      page: currentPageRef.current,
      perPage: PAGE_SIZE,
      productSlug: 'not-required',
    });

    if (messageResponse.data) {
      const { children: newChildren, numChildren, ...newMessage } = messageResponse.data;
      setMessage(newMessage);
      setChildren(newChildren);
      setTotalChildren(numChildren);
    }
  };

  const fetchInitialData = async (): Promise<void> => {
    setIsInitializing(true);
    setMessage(null);
    setChildren([]);
    await initializeMessage();
    setIsInitializing(false);
  };

  useEffect(() => {
    const fetchCreatorData = async (): Promise<void> => {
      const { data: creatorData } = await getConversationCreatorFn({ conversationSid });
      setCreatorPersonId(creatorData?.personId || null);
      setCreatorSlug(creatorData?.slug || null);
    };
    fetchCreatorData();
  }, [conversationSid, getConversationCreatorFn]);

  useEffect(() => {
    if (userIsInitialized) {
      fetchInitialData();
    }
  }, [userId, userIsInitialized, messageId, getMessageThreadFn]);

  // Listen for real-time thread replies
  useEffect(() => {
    if (!isSocketInitialized || !conversationSid || !messageId) {
      return () => {};
    }

    const handleNewThreadReply = async (data: unknown): Promise<void> => {
      const { authorId, messageId: incomingMessageId, parentId } = data as NewChatMessageData;

      // Only process messages that are replies to the current thread
      // parentId === messageId means it's a direct reply to the thread's root message
      if (parentId !== messageId || authorId === userId) {
        return;
      }

      // Fetch the full message data
      const { data: newMessage } = await getConversationMessageFn({
        conversationSid,
        messageId: incomingMessageId,
      });

      if (newMessage) {
        // Check if message already exists to avoid duplicates
        setChildren((prevChildren) => {
          const messageExists = prevChildren.some((child) => child.id === incomingMessageId);
          if (messageExists) {
            return prevChildren;
          }

          // Format the new message to match the expected structure
          const formattedMessage: Children[number] = {
            children: [],
            createdAt: newMessage.createdAt,
            id: newMessage.id,
            isDeleted: newMessage.isDeleted ?? false,
            medias: (newMessage.medias ?? []) as Medias,
            owner: newMessage.owner ?? null,
            parent: null,
            product: null,
            products: [],
            rank: 0,
            richText: newMessage.richText ?? null,
            text: newMessage.text,
            title: newMessage.title ?? null,
            type: newMessage.type,
            upgradeTiers: [],
          };

          // Add the new message to the beginning of the children array
          // (newest messages appear first in the thread)
          const updatedChildren = [formattedMessage, ...prevChildren];

          // Update total children count
          setTotalChildren((prev) => prev + 1);

          return updatedChildren;
        });
      }
    };

    const listenerId = addListener({
      fn: handleNewThreadReply,
      messageType: SocketMessageType.NEW_CHAT_MESSAGE,
    });

    return () => {
      removeListener(listenerId);
    };
  }, [addListener, removeListener, isSocketInitialized, conversationSid, messageId, userId, getConversationMessageFn]);

  if (!message || !creatorPersonId || !creatorSlug || isInitializing) {
    return <ProgressIndicator isPage isCentered />;
  }

  const handleLoadMore = async (): Promise<void> => {
    currentPageRef.current += 1;
    const messageResponse = await getMessageThreadFn({
      creatorSlug: 'not-required',
      messageId,
      page: currentPageRef.current,
      perPage: PAGE_SIZE,
      productSlug: 'not-required',
    });
    if (messageResponse.data) {
      const { children: newChildren, numChildren } = messageResponse.data;
      setChildren((previousChildren) => {
        const forRemovingDuplicates = previousChildren.reduce<Record<string, boolean>>((acc, msg) => {
          acc[msg.id] = true;
          return acc;
        }, {});
        return [...previousChildren, ...newChildren.filter((msg) => !forRemovingDuplicates[msg.id])];
      });
      setTotalChildren(numChildren);
    }
  };

  const refetchMessagesAfterReply = async ({
    parentId,
    attachments,
    text,
    medias,
    handbooks,
  }: {
    parentId: string;
    attachments?: ApiModels.CreateMessageAttachment[];
    text?: { children: Descendant[] }[];
    medias?: { id: string }[];
    handbooks?: { id: string }[];
  }): Promise<void> => {
    if (text?.length || medias?.length || handbooks?.length) {
      setIsPostingComment(true);
      const newMessage = await postConversationMessageFn({
        attachments,
        conversationSid,
        handbooks,
        medias,
        messageId: parentId,
        messageType: ApiModels.MessageType.Chat,
        sendAsCreator: false,
        text: text ? { children: text } : null,
      });
      if (userIsInitialized && newMessage.data) {
        setChildren([
          {
            ...newMessage.data,
            children: [],
            medias: (newMessage.data.medias ?? []) as Medias,
            parent: null,
            products: [],
            rank: 0,
            upgradeTiers: [],
          },
          ...children,
        ]);
        onSendReply();
      }
      setIsPostingComment(false);
    }
  };

  const canFetchMore = children.length < totalChildren;

  const onSend = async ({
    text,
    attachments,
    medias,
  }: {
    text?: { children: Descendant[] }[];
    attachments?: ApiModels.CreateMessageAttachment[];
    medias?: { id: string }[];
  }): Promise<void> => {
    if (currentMessage) {
      if (editMessageFn) {
        await editMessageFn({
          attachments,
          id: currentMessage.id,
          medias,
          text,
        });
      }

      setCurrentMessage(null);

      await initializeMessage();
    } else {
      await refetchMessagesAfterReply({
        attachments,
        medias,
        parentId: message.id,
        text,
      });
    }
  };

  return (
    // TODO(will): The parent of this element is a roleless div; fix the layout so we don't have to guess.
    <Chat className='px-2 min-h-0 h-full max-h-[calc(100dvh-4.5rem)]'>
      <ChatContent>
        <ConversationMessage
          message={{
            ...message,
            children,
          }}
          messageId={messageId}
          creatorPersonId={creatorPersonId}
          isThreadDetail
          isEmbeddedFromModal
          creatorSlug={creatorSlug}
          refetchMessage={initializeMessage}
          canDeleteMessages={true}
          onDeleteMessage={handleDeleteMessage}
          canPinMessages={false}
          canReplyToMessages={false}
          isConversationThread
          canEditMessages={true}
          setCurrentMessage={setCurrentMessage}
          currentMessage={{
            ...message,
            isAIGenerated: message.isAIGenerated ?? false,
            numChildren: message.numChildren ?? 0,
          }}
          onCloseThread={onCloseThread}
        />
      </ChatContent>
      <ConversationComposeBox
        showAddVideo
        onSend={onSend}
        showProTips={false}
        isFetching={isPostingComment}
        placeholder={'Type your reply'}
        showAddHandbook={hasCreatorPermissions}
        isLoomEnabled={hasCreatorPermissions}
        referenceType={'comment-message'}
        referenceId={message.id}
        currentMessage={currentMessage}
      />
      {!message.areCommentsHidden && canFetchMore && (
        <PaginationObserver isDisabled={!canFetchMore} loadFn={handleLoadMore} />
      )}
    </Chat>
  );
  // return null;
};

export default ConversationThreadPage;
